import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { StatusBadge } from "@/components/StatusBadge";
import { ErrorState, Skeleton } from "@/components/BookingShell";
import { formatPrice, toISODate } from "@/lib/format";

const STATUSES = ["all", "pending", "approved", "canceled", "rejected", "no-show"];

export function StaffAppointments() {
    const [params, setParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const status = params.get("status") || "all";
    const q = params.get("q") || "";
    const startDate = params.get("start_date") || toISODate(new Date());
    const endDate = params.get("end_date") || toISODate(new Date(Date.now() + 90 * 86400000));

    useEffect(() => {
        setError(null); setData(null);
        staffApi.listAppointments({ start_date: startDate, end_date: endDate }).then(setData).catch(setError);
    }, [startDate, endDate]);

    const list = useMemo(() => {
        let items = data?.appointments || [];
        if (status !== "all") items = items.filter((a) => (a.status || "").toLowerCase() === status);
        if (q) {
            const nq = q.toLowerCase();
            items = items.filter((a) =>
                `${a.customer.firstName || ""} ${a.customer.lastName || ""} ${a.customer.email || ""} ${a.customer.phone || ""} ${a.serviceName || ""}`
                    .toLowerCase().includes(nq)
            );
        }
        return items;
    }, [data, status, q]);

    const set = (k, v) => {
        const p = new URLSearchParams(params);
        if (v) p.set(k, v); else p.delete(k);
        setParams(p);
    };

    return (
        <div className="space-y-6" data-testid="staff-appointments">
            <div>
                <h1 className="font-serif font-light text-4xl text-white">Appointments</h1>
                <p className="text-sm text-white/50 mt-1">All bookings, filterable.</p>
            </div>

            <div className="rounded-2xl glass p-4 space-y-4" data-testid="appointments-filters">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" strokeWidth={1.5} />
                    <input
                        data-testid="appointments-search"
                        placeholder="Search by customer, email, phone, or treatment"
                        value={q}
                        onChange={(e) => set("q", e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-transparent border hairline rounded-full text-white placeholder-white/30 focus:border-gold outline-none text-sm"
                    />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex flex-wrap gap-2">
                        {STATUSES.map((s) => (
                            <button
                                key={s}
                                onClick={() => set("status", s === "all" ? "" : s)}
                                data-testid={`filter-status-${s}`}
                                className={`text-xs uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border ${
                                    (status === s || (s === "all" && !params.get("status")))
                                        ? "bg-gold text-navy border-gold"
                                        : "border-white/15 text-white/60 hover:border-gold/40 hover:text-gold"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/50 ml-auto">
                        <label className="flex items-center gap-1">
                            From
                            <input type="date" value={startDate} onChange={(e) => set("start_date", e.target.value)} data-testid="filter-start-date" className="bg-transparent border hairline rounded-md px-2 py-1 text-white text-xs" />
                        </label>
                        <label className="flex items-center gap-1">
                            To
                            <input type="date" value={endDate} onChange={(e) => set("end_date", e.target.value)} data-testid="filter-end-date" className="bg-transparent border hairline rounded-md px-2 py-1 text-white text-xs" />
                        </label>
                    </div>
                </div>
            </div>

            {error && <ErrorState error={error} onRetry={() => setData(null)} />}
            {!data && !error && <Skeleton className="h-64" />}

            {data && (
                <>
                    <div className="text-xs text-white/50" data-testid="appointments-count">{list.length} appointment{list.length === 1 ? "" : "s"}</div>
                    {list.length === 0 ? (
                        <div className="rounded-2xl glass p-8 text-center text-white/60" data-testid="appointments-empty">No appointments match these filters.</div>
                    ) : (
                        <ul className="space-y-2" data-testid="appointments-list">
                            {list.map((a) => (
                                <li key={a.id}>
                                    <Link to={`/staff/appointments/${a.id}`} data-testid={`appt-row-${a.id}`} className="block rounded-2xl border hairline p-4 hover:border-gold/60 hover:bg-white/[0.03] transition-all">
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-3 text-xs">
                                                    <span className="font-ui text-white/60">#{a.id}</span>
                                                    <StatusBadge status={a.status} />
                                                </div>
                                                <div className="font-serif text-lg text-white truncate mt-1">{a.customer.firstName} {a.customer.lastName}</div>
                                                <div className="text-xs text-white/50 truncate">
                                                    {a.serviceName || `Service #${a.serviceId}`} · {a.bookingStart}
                                                </div>
                                                {a.customer.phone && <div className="text-[10px] text-white/40">{a.customer.phone} · {a.customer.email}</div>}
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="text-white/80 font-ui text-sm">{formatPrice(a.price)}</div>
                                                {a.payment?.status && <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">{a.payment.status}</div>}
                                            </div>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
}
